/**
 * Article image carousel (ACF flexible layout: image_carousel_section).
 *
 * Reuses the Swiper instance already bundled for the other sliders - see
 * ../sliders/swiper-base.js, which registers Navigation, Pagination and
 * Scrollbar. Autoplay is added per-instance below rather than globally, so
 * swiper-base.js (shared by every other slider) stays untouched.
 *
 * Settings come from data-* attributes on the track, matching the contract used
 * by image_box_slider.js and post_grid.js. Every one has a fallback, so the
 * carousel behaves exactly as before if the ACF sub-fields do not exist yet.
 *
 * Differences from slider_type1.js, all deliberate:
 *  - binds on DOMContentLoaded rather than setTimeout(..., 100);
 *  - scopes the scrollbar element to the instance (as post_grid.js does) rather
 *    than the global ".swiper-scrollbar";
 *  - navigation selectors are built from the section's own id, which single.php
 *    derives from the post id plus the flexible-content row index, so two
 *    carousels in one article control themselves and not each other.
 */

import Swiper from "../sliders/swiper-base.js";
import { Autoplay } from "swiper/modules";

var TRACK_SELECTOR = ".image_carousel_section__track.slider-init";
var ROOT_SELECTOR = ".image_carousel_section";

/** Breakpoint at which the desktop slides-per-view takes over. Matches slider_type1.js. */
var DESKTOP_BREAKPOINT = 700;

function num(el, name, fallback) {
    var raw = el.getAttribute("data-" + name);
    if (raw === null || raw === "") return fallback;
    var n = parseFloat(raw);
    return isFinite(n) ? n : fallback;
}

function bool(el, name, fallback) {
    var raw = el.getAttribute("data-" + name);
    if (raw === null || raw === "") return fallback;
    return raw === "1" || raw === "true";
}

function initImageCarousels() {
    var tracks = document.querySelectorAll(TRACK_SELECTOR);

    for (var i = 0; i < tracks.length; i++) {
        var track = tracks[i];

        // Already initialised (double DOMContentLoaded, or re-entry).
        if (track.swiper) {
            continue;
        }

        var root = track.closest(ROOT_SELECTOR);
        if (!root || !root.id) {
            continue; // Without an id the navigation selectors cannot be scoped.
        }

        // A single slide is rendered as a plain figure by single.php, so this is
        // only a guard against hand-edited or filtered markup.
        var slideCount = track.querySelectorAll(".swiper-slide").length;
        if (slideCount < 2) {
            continue;
        }

        var spv = num(track, "slides_per_view", 2);
        var spvMobile = num(track, "slides_per_view_mobile", 1.2);
        var space = num(track, "space_between", 20);
        var speed = num(track, "transition_speed", 400);
        var autoplay = bool(track, "autoplay", false);
        var delay = num(track, "autoplay_delay", 4000);
        var loop = bool(track, "loop", false);

        // Looping needs enough slides to fill a viewport twice over, otherwise
        // Swiper warns and disables it anyway.
        if (loop && slideCount < Math.ceil(spv) * 2) {
            loop = false;
        }

        var scrollbarEl = track.querySelector(".swiper-scrollbar");
        var options = {
            modules: [Autoplay],
            loop: loop,
            speed: speed,
            watchOverflow: true,
            slidesPerView: spvMobile,
            spaceBetween: space,
            breakpoints: {},
            navigation: {
                nextEl: "#" + root.id + " .swiper__next",
                prevEl: "#" + root.id + " .swiper__prev"
            }
        };
        options.breakpoints[DESKTOP_BREAKPOINT] = {
            slidesPerView: spv,
            spaceBetween: space
        };

        if (scrollbarEl) {
            options.scrollbar = { el: scrollbarEl, hide: false };
        }

        if (autoplay) {
            options.autoplay = {
                delay: delay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            };
        }

        new Swiper(track, options);
    }
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initImageCarousels);
} else {
    initImageCarousels();
}
